/**
 * `gamification-core` — CONFIG del dominio comandero (POS de restaurante).
 *
 * Retrofit OPT-IN y NO punitivo (B2B, orientado al EQUIPO): la gamificación
 * celebra el buen servicio del turno (órdenes cerradas, cuadre de caja correcto,
 * constancia de días con servicio) como ENGAGEMENT del equipo — nunca como
 * vigilancia de la persona. Ligas privadas por defecto (§leagues): no se muestran
 * comparativas entre personas.
 *
 * La gamificación DERIVA del `activity` (bucket `activity_recent`) — ver
 * `derive.ts`. No altera el log ni añade tablas: es una proyección pura y
 * recomputable.
 */
import { counterAtLeast, streakAtLeast } from "./badges";
import type { GamificationConfig } from "./engine";

/** Tipos de evento de dominio (derivados del feed de `activity`). */
export const COMANDERO_EVENTS = {
  /** Una orden se cobró/cerró (status → "paid"). */
  orderClosed: "order.closed",
  /** Se cerró (cuadró) una sesión de caja del turno (status → "closed"). */
  shiftReconciled: "shift.reconciled",
} as const;

/** Línea de racha: días con al menos una orden cerrada (constancia del equipo). */
export const SERVICE_STREAK_LINE = "service";

export const comanderoGamificationConfig: GamificationConfig = {
  xp: {
    [COMANDERO_EVENTS.shiftReconciled]: 30,
    [COMANDERO_EVENTS.orderClosed]: 5,
  },
  streakLines: {
    [COMANDERO_EVENTS.orderClosed]: [SERVICE_STREAK_LINE],
  },
  counters: {
    [COMANDERO_EVENTS.orderClosed]: ["orders_closed"],
    [COMANDERO_EVENTS.shiftReconciled]: ["shifts_reconciled"],
  },
  badges: [
    {
      id: "first-order",
      title: "First order",
      description: "Close your first order.",
      when: counterAtLeast("orders_closed", 1),
    },
    {
      id: "first-shift",
      title: "First shift closed",
      description: "Close and reconcile your first cash session.",
      when: counterAtLeast("shifts_reconciled", 1),
    },
    {
      id: "hundred-orders",
      title: "Hundred orders",
      description: "Close one hundred orders as a team.",
      when: counterAtLeast("orders_closed", 100),
    },
    {
      id: "steady-week",
      title: "Steady week",
      description: "Serve orders seven days in a row.",
      when: streakAtLeast(SERVICE_STREAK_LINE, 7),
      bonusXp: 50,
    },
    {
      id: "balanced",
      title: "Balanced",
      description: "Reconcile ten cash sessions.",
      when: counterAtLeast("shifts_reconciled", 10),
    },
  ],
  quests: [
    {
      id: "daily-orders",
      title: "Close ten orders today",
      period: "daily",
      goals: [{ metric: COMANDERO_EVENTS.orderClosed, target: 10 }],
      bonusXp: 20,
    },
  ],
};
